import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Text } from "../components/catalyst/text";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
  CardDescription,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Header } from "../layout/Header";
import { Calendar, Route, MapPin, User, Clipboard, Clock, ChevronRight, CheckCircle2, Users } from "lucide-react";
import { ScrollArea } from "../components/ui/scroll-area";
import { format, parseISO } from "date-fns";
import useRsmSchedules from "../hooks/useRsmSchedules";
import MapSection from "../components/MapSection";
import AllSchedulesMapSection from "../components/AllSchedulesMapSection";
import { 
  Dialog, 
  DialogTrigger, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from "../components/ui/dialog";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import StopContacts from "../components/StopContacts";

// Progress status colors
const statusColors = {
  PENDING: "bg-muted",
  IN_PROGRESS: "bg-blue-500",
  COMPLETED: "bg-green-500",
  CANCELLED: "bg-red-500",
  RESCHEDULED: "bg-amber-500"
};

function RsmSchedulesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    schedules,
    loading,
    error,
    selectedScheduleId,
    selectedSchedule,
    userData,
    setSelectedScheduleId,
    updateStopProgress
  } = useRsmSchedules();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [stopProgressOpen, setStopProgressOpen] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopStatus, setStopStatus] = useState("");
  const [stopNotes, setStopNotes] = useState("");
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [selectedStopForContacts, setSelectedStopForContacts] = useState(null);

  
  // Create directionsRequest for route optimization similar to BuildItineraryPage
  const directionsRequest = useMemo(() => {
    if (!selectedSchedule?.itinerary?.customers) return null;
    
    const customers = selectedSchedule.itinerary.customers;
    if (customers.length < 1) return null;

    const waypoints = customers
      .map((c) => ({ location: c.location }))
      .filter((wp) => wp.location);

    if (waypoints.length < 1) return null;

    // Create optimal route connecting all customer stops in sequence
    if (waypoints.length === 1) {
      return null; // Single customer - no route needed
    } else if (waypoints.length === 2) {
      return {
        origin: waypoints[0],
        destination: waypoints[1],
        waypoints: [],
        optimizeWaypoints: false,
        travelMode: "DRIVING",
        routeType: "simple"
      };
    } else {
      return {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1], 
        waypoints: waypoints.slice(1, -1),
        optimizeWaypoints: true,
        travelMode: "DRIVING",
        routeType: "optimized"
      };
    }
  }, [selectedSchedule]);

  // Debug output
  console.log("RsmSchedulesPage render - User:", user?.username);
  console.log("RsmSchedulesPage - Schedules:", schedules?.length);
  console.log("RsmSchedulesPage - Loading:", loading);
  console.log("RsmSchedulesPage - Error:", error);

  const handleScheduleClick = (scheduleId) => {
    setSelectedScheduleId(scheduleId);
  };

  const handleViewDetails = () => {
    if (selectedScheduleId) {
      // Debug: Log the schedule ID being used for navigation
      console.log("Navigating to schedule with ID:", selectedScheduleId, "Type:", typeof selectedScheduleId);
      navigate(`/schedules/${selectedScheduleId}`);
    }
  };

  const handleOpenProgressDialog = (stop) => {
    setSelectedStop(stop);
    setStopStatus(stop.status || "PENDING");
    setStopNotes(stop.notes || "");
    setStopProgressOpen(true);
  };

  const handleViewContacts = (stop) => {
    setSelectedStopForContacts(stop);
    setContactsDialogOpen(true);
  };

  const handleUpdateStopProgress = async () => {
    if (!selectedStop || !selectedStop.stop_id) return;
    
    const success = await updateStopProgress(
      selectedStop.stop_id,
      stopStatus,
      stopNotes
    );
    
    if (success) {
      setStopProgressOpen(false);
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "secondary";
      case "low":
        return "default";
      default:
        return "outline";
    }
  };

  // Calculate progress for the selected schedule
  const calculateProgress = (schedule) => {
    if (!schedule?.itinerary?.customers || schedule.itinerary.customers.length === 0) {
      return 0;
    }

    const customers = schedule.itinerary.customers;
    const completed = customers.filter(c => c.status === "COMPLETED").length;
    const inProgress = customers.filter(c => c.status === "IN_PROGRESS").length;
    
    return Math.round((completed + (inProgress * 0.5)) / customers.length * 100);
  };


  // If there's a loading error, display it
  if (error && !loading) {
    return (
      <div>
        <Header />
        <div className="p-8 text-center">
          <div className="mb-4 text-xl font-semibold">Error Loading Schedules</div>
          <div className="text-red-500 mb-4">{error}</div>
          <Button 
            onClick={() => window.location.reload()}
            variant="outline"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      
      <div className="mb-4">
        <Tabs defaultValue="dashboard" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="schedules">My Schedules</TabsTrigger>
          </TabsList>
          
          <TabsContent value="dashboard" className="space-y-4 mt-4">
            {/* Welcome Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Welcome, {userData?.full_name || user?.full_name || "RSM"}
                </CardTitle>
                <CardDescription>
                  Manage your assigned schedules and track customer visits
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="py-4 text-center text-muted-foreground">
                    Loading your data...
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Assigned Schedules</span>
                      <span className="text-2xl font-bold">{schedules?.length || 0}</span>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Customer Stops</span>
                      <span className="text-2xl font-bold">
                        {schedules?.reduce((total, schedule) => 
                          total + (schedule.itinerary?.customers?.length || 0), 0) || 0}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Recent Schedules */}
            {!loading && schedules && schedules.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Schedules</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {schedules.slice(0, 3).map((schedule) => {
                    const progress = calculateProgress(schedule);
                    
                    return (
                      <div 
                        key={schedule.id || schedule.cluster}
                        className="p-4 border-b last:border-0 cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelectedScheduleId(schedule.id || schedule.cluster);
                          setActiveTab("schedules");
                        }}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="font-medium">
                              {schedule.metadata?.itinerary_name || `Schedule ${schedule.cluster}`}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {schedule.itinerary?.customers?.length || 0} customer stops
                            </p>
                          </div>
                          <Badge variant={getPriorityBadgeVariant(schedule.priority)}>
                            {schedule.priority || "N/A"}
                          </Badge>
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>Progress</span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
                <CardFooter className="pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => setActiveTab("schedules")}
                  >
                    View All Schedules
                  </Button>
                </CardFooter>
              </Card>
            ) : !loading && (!schedules || schedules.length === 0) ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">No Assigned Schedules</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    You don't have any schedules assigned to you yet. Please contact your manager.
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </TabsContent>
          
          <TabsContent value="schedules" className="mt-4">
            {/* All Schedules Overview Map */}
            {schedules && schedules.length > 0 && (
              <Card className="overflow-hidden h-[400px] mb-6">
                <CardHeader className="p-4 border-b">
                  <CardTitle className="text-lg font-semibold">
                    All Assigned Schedules Overview Map
                  </CardTitle>
                  <CardDescription>
                    Showing all customer stops across {schedules.length} assigned schedules
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0 h-full">
                  <AllSchedulesMapSection schedules={schedules} />
                </CardContent>
              </Card>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <Card className="flex flex-col h-full">
                  <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                    <CardTitle className="text-base font-semibold">
                      Assigned Schedules ({schedules?.length || 0})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0 flex-1 overflow-hidden">
                    <ScrollArea className="h-[calc(100vh-220px)]">
                      <div className="divide-y">
                        {loading ? (
                          <div className="p-4 text-center">Loading schedules...</div>
                        ) : error ? (
                          <div className="p-4 text-center text-red-500">{error}</div>
                        ) : schedules && schedules.length > 0 ? (
                          schedules.map((schedule) => {
                            const progress = calculateProgress(schedule);
                            
                            return (
                              <div
                                key={schedule.id || schedule.cluster}
                                className={`px-3 py-2 sm:px-4 sm:py-3 cursor-pointer transition-colors hover:bg-muted ${
                                  selectedScheduleId === (schedule.id || schedule.cluster)
                                    ? "bg-muted"
                                    : ""
                                }`}
                                onClick={() => handleScheduleClick(schedule.id || schedule.cluster)}
                              >
                                <div className="flex justify-between items-start gap-2 mb-2">
                                  <div>
                                    <div className="font-medium">
                                      {schedule.metadata?.itinerary_name ||
                                        `Schedule ${schedule.cluster}`}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                      Assigned: {format(new Date(schedule.assigned_at || schedule.created_at), "MMM d, yyyy")}
                                    </div>
                                  </div>
                                  <Badge
                                    variant={getPriorityBadgeVariant(schedule.priority)}
                                    className="uppercase text-[10px]"
                                  >
                                    {schedule.priority || "N/A"}
                                  </Badge>
                                </div>
                                
                                <div className="space-y-1">
                                  <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>Progress</span>
                                    <span>{progress}%</span>
                                  </div>
                                  <Progress value={progress} className="h-2" />
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-4 text-center text-muted-foreground">
                            No schedules assigned to you.
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-2">
                {selectedSchedule ? (
                  <div className="space-y-4">
                    {/* Map Card */}
                    <Card className="overflow-hidden h-[300px]">
                      <CardContent className="p-0 h-full relative">
                        <MapSection
                          customers={selectedSchedule.itinerary?.customers || []}
                          directionsRequest={directionsRequest}
                        />
                      </CardContent>
                    </Card>
                    
                    {/* Schedule Info & Progress */}
                    <Card>
                      <CardHeader className="flex flex-row justify-between items-center p-4 border-b">
                        <div>
                          <CardTitle className="text-base font-semibold">
                            {selectedSchedule.metadata?.itinerary_name ||
                              `Schedule ${selectedSchedule.cluster}`}
                          </CardTitle>
                          <CardDescription>
                            {selectedSchedule.itinerary?.customers?.length || 0} customer stops
                          </CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleViewDetails}
                        >
                          <Route className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Progress</span>
                              <span className="font-medium">{calculateProgress(selectedSchedule)}%</span>
                            </div>
                            <Progress value={calculateProgress(selectedSchedule)} className="h-2" />
                          </div>
                          
                          <div>
                            <h3 className="text-sm font-medium mb-3">Customer Stops</h3>
                            {selectedSchedule.itinerary?.customers?.length > 0 ? (
                              <div className="space-y-3">
                                {selectedSchedule.itinerary.customers.map((customer, index) => (
                                  <div 
                                    key={customer.stop_id || index}
                                    className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 border rounded-md hover:bg-muted/50"
                                  >
                                    <div className={`w-1.5 sm:w-2 h-full min-h-[40px] ${statusColors[customer.status || "PENDING"]}`} />
                                    <div className="flex-1 cursor-pointer" onClick={() => handleOpenProgressDialog(customer)}>
                                      <div className="font-medium">{customer.name}</div>
                                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {customer.address}, {customer.city}, {customer.state}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewContacts(customer);
                                        }}
                                      >
                                        <Users className="h-4 w-4" />
                                      </Button>
                                      <Badge 
                                        variant="outline" 
                                        className={`${
                                          customer.status === "COMPLETED" 
                                            ? "bg-green-500/10 text-green-700 border-green-500/20" 
                                            : customer.status === "IN_PROGRESS"
                                            ? "bg-blue-500/10 text-blue-700 border-blue-500/20"
                                            : "bg-muted/50"
                                        }`}
                                      >
                                        {customer.status || "PENDING"}
                                      </Badge>
                                      <ChevronRight className="ml-2 h-4 w-4 text-muted-foreground" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="p-4 text-center text-muted-foreground border rounded-md">
                                No customer stops in this schedule.
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-6 text-center">
                      <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <Text className="text-lg font-medium">No Schedule Selected</Text>
                      <Text className="text-muted-foreground mt-1">
                        {schedules && schedules.length > 0 
                          ? "Select a schedule from the list to view its details" 
                          : "No schedules have been assigned to you yet"}
                      </Text>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Stop Progress Dialog */}
      <Dialog open={stopProgressOpen} onOpenChange={setStopProgressOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Customer Stop Status</DialogTitle>
            <DialogDescription>
              Update status and add notes for {selectedStop?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={stopStatus} onValueChange={setStopStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">PENDING</SelectItem>
                  <SelectItem value="IN_PROGRESS">IN PROGRESS</SelectItem>
                  <SelectItem value="COMPLETED">COMPLETED</SelectItem>
                  <SelectItem value="CANCELLED">CANCELLED</SelectItem>
                  <SelectItem value="RESCHEDULED">RESCHEDULED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea 
                value={stopNotes} 
                onChange={(e) => setStopNotes(e.target.value)}
                placeholder="Add notes about this customer stop"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStopProgressOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStopProgress}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contacts Dialog */}
      <Dialog open={contactsDialogOpen} onOpenChange={setContactsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contacts for {selectedStopForContacts?.name}</DialogTitle>
            <DialogDescription>
              View and manage contacts associated with this stop. Select contacts to sync engagement data.
            </DialogDescription>
          </DialogHeader>
          
          {selectedStopForContacts && (
            <StopContacts
              stopId={selectedStopForContacts.stop_id}
              customerId={selectedStopForContacts.customer_id || selectedStopForContacts.id}
              onUpdate={() => {
                // Optionally refresh data
                console.log("Contacts updated");
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RsmSchedulesPage; 